import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CFormSelect, CFormSwitch, CButton, CAlert,
  CCard, CCardHeader, CCardBody,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CInputGroup, CInputGroupText,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import CIcon from '@coreui/icons-react'
import { cilStar, cilUser, cilDollar, cilLocationPin, cilLockLocked } from '@coreui/icons'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { usePendiente } from '../components/ww/pendiente'

const columnas: WwColumn[] = [
  { key: 'cobradorCodigo', title: 'Codigo', type: 'number' },
  { key: 'usuario', title: 'Usuario' },
  { key: 'nombre', title: 'Nombre' },
  { key: '_clasica', title: 'Clasica', type: 'bool' },
  { key: 'cobradorHaceRenovacion', title: 'Renovacion?' },
  { key: 'cobradorCreaCliente', title: 'Clientes?' },
  { key: 'cobradorHaceCierre', title: 'Cierre?' },
  { key: 'cobradorBorrar', title: 'Modificar' },
  // títulos CLAROS [RGM] 2026-10-05 (pedido Rafael: "no sé si es el mismo
  // campo vales y faltantes"): el GX titula CobradorFaltantes como "Balance
  // SOS" (cobradorww.cs:2561) y CobradorVales como "Pagos SOS" (:2562) —
  // MISMOS campos; además existe el campo REAL CobradorBalanceSOS (:2567, GX
  // también lo titula "Balance SOS") que antes no se mostraba.
  { key: 'cobradorFaltantes', title: 'Faltantes', type: 'currency' },
  { key: 'cobradorVales', title: 'Vales', type: 'currency' },
  { key: 'cobradorBalanceSOS', title: 'Balance SOS', type: 'currency' },
  { key: 'cobradorGeolocalizacion', title: 'Geolocalizacion' },
  { key: 'cobradorCalificacion', title: 'Calificacion', type: 'number' },
  { key: 'empresaCodigo', title: 'Empresa Codigo' },
  { key: 'empresaNombre', title: 'Empresa Nombre' },
]

const CobradoresPage: React.FC = () => {
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // Catálogo de usuarios de la empresa (el select de Usuario muestra el nombre).
  const [usuarios, setUsuarios] = useState<{ usuarioCodigo: string; usuarioNombre: string }[]>([])
  useEffect(() => {
    api.get<any[]>('/usuarios?empresa=' + empresa).then(setUsuarios).catch(() => undefined)
  }, [empresa])
  // ── Filtros rápidos (WwFilterBar) ──
  const [filtroUsuario, setFiltroUsuario] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Fila en edición (cobradorww.cs:2650-2652 'DO UPDATE' → webobject cobrador Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [errorModal, setErrorModal] = useState('')
  const [form, setForm] = useState({
    usuarioCodigo: '', nombre: '',
    calificacion: 0,
    haceRenovacion: true, creaCliente: true, haceCierre: true,
    faltantes: 0, vales: 0, balanceSOS: 0,
    geolocalizacion: '', borrar: '',
    prestaDiario: 'Si', prestaSemanal: 'Si', prestaQuincenal: 'Si',
    prestaMensual: 'Si', prestaLibre: 'Si',
  })

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroUsuario && String(row.usuario ?? '').trim() !== filtroUsuario) return false
    return true
  }), [rows, filtroUsuario])
  const usuariosCob = useMemo(() => [...new Set(rows.map((r) => String(r.usuario ?? '').trim()).filter(Boolean))], [rows])
  const chips = [filtroUsuario ? { clave: 'usuario', label: `Usuario: ${filtroUsuario}` } : null].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => setFiltroUsuario('')
  const quitarFiltro = (clave: string) => { if (clave === 'usuario') setFiltroUsuario('') }
  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/cobradores?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  const abrirNuevo = () => {
    setEditando(null)
    setForm({
      usuarioCodigo: '', nombre: '',
      calificacion: 0,
      haceRenovacion: true, creaCliente: true, haceCierre: true,
      faltantes: 0, vales: 0, balanceSOS: 0,
      geolocalizacion: '', borrar: '',
      prestaDiario: 'Si', prestaSemanal: 'Si', prestaQuincenal: 'Si',
      prestaMensual: 'Si', prestaLibre: 'Si',
    })
    setAbierto(true)
  }

  // Modificar (cobradorww.cs:2650-2652): carga la fila en el form y abre el modal.
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      usuarioCodigo: fila.usuario ?? '',
      nombre: fila.nombre ?? '',
      calificacion: Number(fila.cobradorCalificacion ?? 0),
      haceRenovacion: String(fila.cobradorHaceRenovacion ?? 'Si').trim() !== 'No',
      creaCliente: String(fila.cobradorCreaCliente ?? 'Si').trim() !== 'No',
      haceCierre: String(fila.cobradorHaceCierre ?? 'Si').trim() !== 'No',
      faltantes: Number(fila.cobradorFaltantes ?? 0),
      vales: Number(fila.cobradorVales ?? 0),
      balanceSOS: Number(fila.cobradorBalanceSOS ?? 0),
      geolocalizacion: fila.cobradorGeolocalizacion ?? '',
      borrar: fila.cobradorBorrar ?? '',
      prestaDiario: (fila.cobradorPrestaDiario ?? 'Si').trim() || 'Si',
      prestaSemanal: (fila.cobradorPrestaSemanal ?? 'Si').trim() || 'Si',
      prestaQuincenal: (fila.cobradorPrestaQuincenal ?? 'Si').trim() || 'Si',
      prestaMensual: (fila.cobradorPrestaMensual ?? 'Si').trim() || 'Si',
      prestaLibre: (fila.cobradorPrestaLibre ?? 'Si').trim() || 'Si',
    })
    setAbierto(true)
  }

  const crear = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    setGuardando(true)
    try {
      if (editando) {
        // cobradorww.cs:2650-2652 — DO UPDATE: webobject cobrador Mode="UPD" con
        // CobradorCodigo (la PK viaja en la URL). El backend replica BC000A9
        // (cobrador_bc.cs:1757). BalanceSOS/Geolocalización siguen deshabilitados
        // en el form GX (cobrador.cs:1318-1339) → no se envían (el backend no
        // los toca). [RGM] 2026-10-05 (pedido Rafael): Faltantes y Vales SÍ
        // viajan — ahora son editables también al modificar.
        await api.put(`/cobradores/${editando.cobradorCodigo}`, {
          empresaCodigo: empresa,
          usuarioCodigo: form.usuarioCodigo,
          calificacion: Number(form.calificacion) || 0,
          haceRenovacion: form.haceRenovacion ? 'Si' : 'No',
          creaCliente: form.creaCliente ? 'Si' : 'No',
          haceCierre: form.haceCierre ? 'Si' : 'No',
          faltantes: Number(form.faltantes) || 0,
          vales: Number(form.vales) || 0,
          borrar: form.borrar || undefined,
          prestaDiario: form.prestaDiario, prestaSemanal: form.prestaSemanal,
          prestaQuincenal: form.prestaQuincenal, prestaMensual: form.prestaMensual,
          prestaLibre: form.prestaLibre,
        })
      } else {
        await api.post('/cobradores', {
          empresaCodigo: empresa,
          usuarioCodigo: form.usuarioCodigo,
          nombre: form.nombre,
          calificacion: Number(form.calificacion) || 0,
          haceRenovacion: form.haceRenovacion ? 'Si' : 'No',
          creaCliente: form.creaCliente ? 'Si' : 'No',
          haceCierre: form.haceCierre ? 'Si' : 'No',
          faltantes: Number(form.faltantes) || 0,
          vales: Number(form.vales) || 0,
          balanceSOS: Number(form.balanceSOS) || 0,
          geolocalizacion: form.geolocalizacion || undefined,
          borrar: form.borrar || undefined,
          prestaDiario: form.prestaDiario, prestaSemanal: form.prestaSemanal,
          prestaQuincenal: form.prestaQuincenal, prestaMensual: form.prestaMensual,
          prestaLibre: form.prestaLibre,
        })
      }
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminar = (fila: any) => {
    // cobradorww.cs:2658-2660 — DO DELETE: webobject cobrador Mode="DLT" con
    // CobradorCodigo. El backend replica OnDeleteControls0A9
    // (cobrador_bc.cs:565-595): Zona del cobrador → 409, sin refs → DELETE
    // (BC000A10).
    if (!window.confirm(`¿Eliminar el cobrador ${fila.cobradorCodigo}?`)) return
    api.delete(`/cobradores/${fila.cobradorCodigo}?empresa=${empresa}`)
      .then(() => {
        setAviso('Cobrador eliminado.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Cobrador</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}
      <WwGrid
        filters={
          <WwFilterBar
            selects={[
              { key: 'usuario', label: 'Todos los Usuarios', opciones: usuariosCob, valor: filtroUsuario, onChange: setFiltroUsuario },
            ]}
            chips={chips}
            onQuitar={quitarFiltro}
            onLimpiar={limpiarFiltros}
          />
        }
        title="Cobrador"
        columns={columnas}
        rows={visibleRows}
        busquedaPlaceholder="Buscar por cobrador..." 
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirNuevo}
        onEditar={abrirEditar}
        onEliminar={eliminar}
        // [RGM] 2026-10-05 (pedido Rafael: "al darle en la fila del cobrador
        // debe llevarme al view"): clic en la fila = vista del cobrador.
        onRowClick={(row) => navigate(`/cobradores/${row.cobradorCodigo}`)}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar cobrador' : 'Nuevo cobrador'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={crear}>
            {/* ── TARJETA 1 — DATOS DEL COBRADOR ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilUser} size="sm" /> Datos del Cobrador
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}>
                    {/* Select del USUARIO por NOMBRE (el backend recibe el código) */}
                    <CFormLabel>Usuario</CFormLabel>
                    {/* [RGM] 2026-10-03 (pedido Rafael): el select muestra el
                        NOMBRE DE USUARIO (login/seudónimo, tal cual se escribe
                        — minúscula); antes mostraba el nombre de pila. */}
                    <CFormSelect
                      value={form.usuarioCodigo}
                      onChange={(e) => {
                        const u = usuarios.find((x) => x.usuarioCodigo === e.target.value)
                        setForm({ ...form, usuarioCodigo: e.target.value, nombre: u ? u.usuarioNombre : '' })
                      }}
                    >
                      <option value="">Seleccione...</option>
                      {usuarios.map((u) => <option key={u.usuarioCodigo} value={u.usuarioCodigo}>{u.usuarioCodigo}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={form.nombre} disabled onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></CCol>
                  <CCol md={6}>
                    <CFormLabel>Calificacion</CFormLabel>
                    {/* Estrellas clicables (0-5) — decisión Rafael. */}
                    <div className="d-flex align-items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <CIcon
                          key={`star-${n}`}
                          icon={cilStar}
                          size="lg"
                          style={{
                            cursor: 'pointer',
                            color: n <= Number(form.calificacion) ? '#F59E0B' : '#ced4da',
                          }}
                          onClick={() => setForm({ ...form, calificacion: n })}
                        />
                      ))}
                      {Number(form.calificacion) > 0 && (
                        <CButton
                          size="sm"
                          color="link"
                          className="text-body-secondary px-1"
                          onClick={() => setForm({ ...form, calificacion: 0 })}
                        >
                          Limpiar
                        </CButton>
                      )}
                    </div>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            {/* ── TARJETA 2 — PERMISOS (decisión Rafael: tarjeta aparte) ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilLockLocked} size="sm" /> Permisos
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-renovacion"
                      label="Hace renovación"
                      checked={form.haceRenovacion}
                      onChange={(e) => setForm({ ...form, haceRenovacion: e.target.checked })}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-cliente"
                      label="Crea cliente"
                      checked={form.creaCliente}
                      onChange={(e) => setForm({ ...form, creaCliente: e.target.checked })}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-cierre"
                      label="Hace cierre"
                      checked={form.haceCierre}
                      onChange={(e) => setForm({ ...form, haceCierre: e.target.checked })}
                    />
                  </CCol>
                  {/* CobradorBorrar = el permiso "Modificar? " del GX
                      (cobrador.cs): habilita modificar Y eliminar. Switch
                      (decisión Rafael). */}
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-modificar"
                      label="Modificar?"
                      checked={form.borrar === 'Si'}
                      onChange={(e) => setForm({ ...form, borrar: e.target.checked ? 'Si' : 'No' })}
                    />
                  </CCol>
                  {/* [RGM] Permisos por modalidad (diseño Rafael): todos los
                      permisos agrupados aquí como switches. */}
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-presta-diario"
                      label="Presta Diario"
                      checked={form.prestaDiario === 'Si'}
                      onChange={(e) => setForm({ ...form, prestaDiario: e.target.checked ? 'Si' : 'No' })}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-presta-semanal"
                      label="Presta Semanal"
                      checked={form.prestaSemanal === 'Si'}
                      onChange={(e) => setForm({ ...form, prestaSemanal: e.target.checked ? 'Si' : 'No' })}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-presta-quincenal"
                      label="Presta Quincenal"
                      checked={form.prestaQuincenal === 'Si'}
                      onChange={(e) => setForm({ ...form, prestaQuincenal: e.target.checked ? 'Si' : 'No' })}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-presta-mensual"
                      label="Presta Mensual"
                      checked={form.prestaMensual === 'Si'}
                      onChange={(e) => setForm({ ...form, prestaMensual: e.target.checked ? 'Si' : 'No' })}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormSwitch
                      id="cob-presta-libre"
                      label="Presta Libre"
                      checked={form.prestaLibre === 'Si'}
                      onChange={(e) => setForm({ ...form, prestaLibre: e.target.checked ? 'Si' : 'No' })}
                    />
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            {/* ── TARJETA 3 — SALDOS ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilDollar} size="sm" /> Saldos
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}><CFormLabel>Faltantes</CFormLabel>
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.faltantes}
                        onValueChange={(v) => setForm({ ...form, faltantes: v.floatValue ?? 0 })}
                        thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                        allowNegative={false} inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={6}><CFormLabel>Vales</CFormLabel>
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.vales}
                        onValueChange={(v) => setForm({ ...form, vales: v.floatValue ?? 0 })}
                        thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                        allowNegative={false} inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            {/* ── TARJETA 3 — OTROS ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilLocationPin} size="sm" /> Otros
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={12}><CFormLabel>Geolocalización</CFormLabel><CFormInput value={form.geolocalizacion} placeholder="lat,lng" disabled={editando !== null} onChange={(e) => setForm({ ...form, geolocalizacion: e.target.value })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>

          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear cobrador'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default CobradoresPage
